// The CAPTURE emitter: (preguntas, embudo, etapa) → a callable task flow.
//
// It MOVED here from `bot-config/orchestrator-scaffold.ts` when the orchestrator
// emitter became the process compiler (Process-Compiled Agent, Wave 2), because
// the compiler emits one of these per connected process (D15) and a compiler
// that had to import the scaffold — which now imports the compiler — would be a
// cycle. Not one character of the builder changed; the scaffold re-exports every
// symbol under its original name, so `@vysto/shared/bot-config/
// orchestrator-scaffold` keeps the exact surface its consumers import today.
//
// PURE + web-safe: no React, no DB, no node builtins.

import {
  BOOKING_CONFIRMED_STATUS,
  type BookingSegment,
  type BookingSegmentIds,
  buildBookingSegment,
  DEFAULT_BOOKING_COPY,
} from './booking-segment.js'
import type { AgentTerminal } from './diff.js'
import type { StarterFlow, StarterNode } from './flow.js'
import type { AskExtract, AskNode } from '../skills/types.js'

// A TASK flow (not the orchestrator): it runs when the classifier matches its
// intent, gathers the three datos a clinic actually needs to call somebody
// back, files the card in the funnel, and gives the turn back. Until this
// existed, the salud starter set could book a cita and quote a price but had
// no way to keep a prospect who was not ready to do either — the recorded
// capture gap of the pilot vertical.
//
// The seeder duplicates this definition as JSON (scripts/seed-demo-data.mjs);
// packages/engine/src/skills/starter-capture-flow.test.ts compares the two
// structurally, so the two homes cannot drift.

/** Name the capture flow is published under. STABLE: the seeder upserts by
 *  (bot_id, name), so renaming it here creates a second flow instead of
 *  updating the one Juan already has on staging. */
export const CAPTURE_FLOW_NAME = 'Captura de prospectos'

/** Intent trigger the classifier dispatches the capture flow on.
 *
 *  MUST be an id the classifier can actually emit — i.e. one of the canonical
 *  `@vysto/shared/intents` vocabulary entries for the bot's vertical. It is a
 *  hand-maintained list, NOT derived from registered flow triggers, so an
 *  invented id here produces a flow that silently never fires. This shipped
 *  once as `captura_prospecto`, which no vertical vocabulary contained: the
 *  classifier's Zod enum could not emit it, so the capture flow was dead on
 *  arrival. `patient_intake` is the existing healthcare intent whose meaning is
 *  exactly this flow's job ("Quiere dejar su nombre, teléfono y motivo de
 *  consulta para que lo contacten"). */
export const CAPTURE_FLOW_INTENT = 'patient_intake'

/** Funnel (pipeline KEY) + landing stage the capture files into. The factory
 *  process every tenant is provisioned with carries both. */
export const CAPTURE_FLOW_FUNNEL = 'prospectos'
export const CAPTURE_FLOW_STAGE = 'calificado'

/** La CLAVE de la categoría de captura del arranque de clínica.
 *
 *  Es un MARCADOR, no una etiqueta: la etiqueta la puede reescribir el cliente
 *  desde el editor, la clave no (es lo que la arista del Descubrir compara). Se
 *  exporta porque la vista previa del agente compilado necesita reconocer esta
 *  rama en un flujo vivo para no presentarla como una opción nueva — ver
 *  `packages/api/src/skills/compiled-preview.ts`. Una copia literal 'datos' del
 *  otro lado sería la deriva de dos casas de siempre. */
export const CAPTURE_CATEGORY_KEY = 'datos'

/** La etiqueta de la categoría de captura del arranque de clínica.
 *
 *  Una etiqueta de Descubrir NO es un título: es lo único que el clasificador
 *  lee para decidir si el mensaje del cliente cae en esta rama. Por eso todas
 *  las del scaffold son frases que describen lo que la PERSONA quiere, en
 *  tercera persona y de usted, y no el nombre de la rama. */
export const CAPTURE_CATEGORY_LABEL =
  'Quiere dejar sus datos o que le llamen después, sin agendar una cita todavía'

/** La etiqueta de la categoría que gana un Descubrir cuando se GENERA el flujo
 *  de captura de un proceso (C1).
 *
 *  Vive pegada a `CAPTURE_CATEGORY_LABEL` a propósito: es la misma frase, con
 *  el proceso puesto donde va. Poner el nombre pelado ("Urgencias") habría sido
 *  una etiqueta más corta y un clasificador peor, porque le quita al modelo
 *  justo lo que usa para decidir. La CLAVE sí es la del proceso, que es lo que
 *  hace la operación idempotente; la etiqueta es para el clasificador. */
export function captureCategoryLabelFor(processName: string): string {
  return `Quiere dejar sus datos para ${processName}, o que le llamen después`
}

/** La operación inversa: qué nombre de proceso lleva DENTRO una etiqueta que
 *  `captureCategoryLabelFor` escribió. Devuelve `null` cuando la etiqueta ya no
 *  tiene esa forma, que es exactamente el caso de una etiqueta editada a mano.
 *
 *  Existe para el detector de deriva (F45): renombrar el proceso deja la opción
 *  del menú diciendo el nombre viejo, y decírselo al cliente exige poder leer
 *  cuál es ese nombre viejo. Vive pegada a la función que escribe la etiqueta
 *  para que las dos no se puedan separar. */
export function processNameInCaptureCategoryLabel(label: string): string | null {
  const m = /^Quiere dejar sus datos para (.+), o que le llamen después$/.exec(label.trim())
  return m ? m[1]! : null
}

/** Cómo se llama el flujo de captura de un proceso. Par de escritura/lectura,
 *  igual que `captureCategoryLabelFor` / `processNameInCaptureCategoryLabel`, y
 *  por la misma razón: si el nombre se arma en un lado y se desarma en otro, los
 *  dos se separan. */
export const CAPTURE_FLOW_NAME_PREFIX = 'Captura '

export function captureFlowNameFor(processName: string): string {
  return `${CAPTURE_FLOW_NAME_PREFIX}${processName}`
}

/** Qué proceso lleva dentro el nombre de un flujo de captura, o `null` cuando el
 *  nombre ya no tiene esa forma (uno escrito a mano).
 *
 *  R1 lo necesita en el RUNTIME: cuando la captura pide la zona, el Descubrir
 *  que la trajo pasó turnos atrás y su etiqueta ya no está a la mano, pero el
 *  flujo activo se sigue llamando "Captura Urgencias E2E 905956". Ese nombre es
 *  lo único que queda diciendo de qué proceso hablamos, y es exactamente la
 *  frase que el cliente repitió y que no se debe minar como respuesta. */
export function processNameInCaptureFlowName(flowName: string): string | null {
  const trimmed = flowName.trim()
  if (!trimmed.startsWith(CAPTURE_FLOW_NAME_PREFIX)) return null
  const name = trimmed.slice(CAPTURE_FLOW_NAME_PREFIX.length).trim()
  return name === '' ? null : name
}

/** Una pregunta del flujo de captura: qué se pregunta, en qué variable se
 *  guarda la respuesta, y cómo se interpreta.
 *
 *  `interest` marca el dato de INTERÉS del giro (Especialidad, Modelo de
 *  interés, Artículo). Su respuesta viaja en el argumento `interes` de
 *  `capturar_lead`, NUNCA dentro de `campos`: su valor vive en una columna del
 *  prospecto (la que pintan el área del tablero, el chip de la tarjeta y los
 *  filtros), y meterlo en la bolsa de datos es la deriva de dos casas que la
 *  biblioteca de campos existe para terminar. */
/** LAS ESPECIALIDADES DE ARRANQUE. YA NO SON LA LISTA DE NINGÚN ESPACIO (D21-4).
 *
 *  Hasta D21-4 este arreglo era dos cosas a la vez: las opciones del dato
 *  `interes` de salud y los valores que acepta la pregunta de especialidad del
 *  flujo de FÁBRICA. La primera se acabó: el preset de salud es ahora una lista
 *  con FUENTE (`options_source: 'agenda'`), o sea las especialidades de los
 *  médicos activos del espacio, y no guarda copia. Una clínica que da de alta a
 *  un neumólogo puede ofrecer Neumología sin que nadie vaya a teclearla.
 *
 *  LE QUEDAN DOS PAPELES, los dos legítimos:
 *
 *    (a) LAS SUGERENCIAS DE LA PANTALLA DE AGENDA. El campo "Especialidad" del
 *        alta de un doctor es texto libre y se queda así — una clínica escribe
 *        lo que su médico es —, pero ofrece estas siete como `datalist`. Es
 *        donde se evita que la fuente se fragmente en el punto de captura
 *        ('Cardiologo' y 'Cardiología' como dos especialidades distintas), sin
 *        obligar a nadie a elegir de una lista que no es suya.
 *
 *    (b) LOS VALORES HORNEADOS DEL FLUJO DE FÁBRICA (`FACTORY_CAPTURE_QUESTIONS`
 *        abajo). Ese flujo se arma SIN espacio y sin base — es el cuerpo que el
 *        seeder de demo duplica y que `starter-capture-flow.test.ts` compara
 *        byte por byte —, así que no puede resolver la Agenda de nadie: tiene
 *        que traer valores literales o no puede preguntar como `enum`.
 *
 *  Sigue viviendo aquí por la razón mecánica de siempre: el flujo de fábrica se
 *  arma en `@vysto/shared` y `@vysto/shared` no puede importar `@vysto/engine`.
 *  Lo que cambió es que `field-presets.ts` ya NO la importa, porque el preset ya
 *  no lleva lista. Un flujo generado desde la ficha lee las opciones VIGENTES
 *  del catálogo (C2) — resueltas contra la fuente —, nunca esta constante. */
export const HEALTHCARE_SPECIALTIES: readonly string[] = [
  'Medicina general',
  'Cardiología',
  'Dermatología',
  'Ginecología',
  'Neurología',
  'Pediatría',
  'Traumatología',
]

export interface CaptureFlowQuestion {
  /** Variable del flujo Y clave del dato en la ficha del proceso. */
  var: string
  prompt: string
  /** `ask.config.extract` (kind + su configuración). TIPADO, no
   *  `Record<string, unknown>`: la tabla de C2 devuelve exactamente esta unión,
   *  y un `{kind:'enum'}` sin `values` pasaría el validador de flujos (que sólo
   *  revisa que el kind exista) para romperse en la conversación. */
  extract: AskExtract
  interest?: boolean
  /** ESTA PREGUNTA BAUTIZA LA TARJETA (F5-1). La hermana de `interest`, y por el
   *  mismo motivo: su respuesta viaja en un argumento propio de `capturar_lead`
   *  (`nombre`) y NUNCA dentro de `campos`, porque vive en una columna del
   *  prospecto (`leads.name`, el título de la tarjeta) y no en la bolsa.
   *
   *  El emisor no la lee: recibe `nameVar` ya resuelto, porque el flujo de
   *  FÁBRICA se arma sin plan y ahí quién es el nombre lo dice la posición. La
   *  marca es de quien SÍ arma un plan desde una ficha — de ella sale
   *  `plan.nameVar` — para que la respuesta salga de un lugar y no de dos. */
  name?: boolean
  /** Los ajustes AUTORADOS del `ask` que sobreviven a una regeneración (F45).
   *
   *  Regenerar el flujo de un proceso es una FUSIÓN, no un rehacer: el dato
   *  sigue siendo el mismo dato, así que el texto que el cliente escribió para
   *  preguntarlo sigue siendo suyo. `prompt` viaja arriba (es la pregunta); lo
   *  demás que se puede editar en el inspector del paso viaja aquí, tal cual,
   *  para que la fusión no tenga que enumerar campo por campo lo que conserva.
   *  `var` y `extract` NO están: esos los manda la ficha. */
  preserved?: PreservedAskConfig
}

/** Lo editable de un `ask` que la fusión conserva. Es `AskNode['config']` menos
 *  las tres cosas que la ficha decide (`var`, `prompt`, `extract`). */
export type PreservedAskConfig = Partial<
  Pick<AskNode['config'], 'retry_prompt' | 'max_retries' | 'no_prefill' | 'on_unexpected'>
>

/** Lo que cambia entre el flujo de captura de fábrica y uno GENERADO desde la
 *  ficha de un proceso. Sin opciones, `buildCaptureFlow` devuelve exactamente el
 *  flujo de fábrica: el seeder de demo lo duplica como JSON y
 *  `starter-capture-flow.test.ts` compara las dos casas. */
export interface CaptureFlowOptions {
  /** Las preguntas, en orden. Su primer nodo se llama `start` (el validador
   *  pide un nodo con ese id). Hasta F4-7 la primera SIEMPRE era el nombre;
   *  desde F4-7 quién es el nombre lo dice `nameVar`, no la posición. */
  questions?: readonly CaptureFlowQuestion[]
  /** CUÁL DE LAS PREGUNTAS ES EL NOMBRE (F4-7), o `null` cuando el proceso no
   *  lo pregunta.
   *
   *  EXPLÍCITO, y no `questions[0]`, por un caso de PÉRDIDA DE DATOS. Desde que
   *  la pregunta del nombre es removible, la primera pregunta de una captura
   *  puede ser un dato de la ficha. El emisor posicional mandaría el valor de
   *  ESE dato como `nombre` de la tarjeta Y lo sacaría de `campos` (el nombre no
   *  va en la bolsa), así que el dato se perdería en silencio y la tarjeta
   *  quedaría bautizada con una aseguradora.
   *
   *  Ausente = la conducta posicional de siempre (`questions[0].var`), que es lo
   *  que mantiene el flujo de FÁBRICA byte por byte: se arma sin opciones.
   *  `null` = no hay pregunta de nombre, así que `capturar_lead` no lleva el
   *  argumento y lo deriva del perfil del contacto (F3-1a). */
  nameVar?: string | null
  /** Clave del embudo (proceso) donde aterriza la tarjeta. */
  funnel?: string
  /** Clave de la etapa donde aterriza la tarjeta. */
  stage?: string
  /** Lo que dice el agente al confirmar. */
  confirmText?: string
  /** Los TRAMOS: qué preguntas aterrizan en qué etapa (F3-1b).
   *
   *  SIN ellos el emisor es exactamente el de siempre, byte por byte: una sola
   *  tanda de preguntas, un solo `guardar` y el cierre de tres ramas. Con ellos
   *  la tarjeta NACE al entrar (un `capturar_lead` sin datos en la primera
   *  etapa) y va avanzando de columna en columna conforme el agente aprende lo
   *  que cada una obtiene, que es lo que hace el proceso del cliente. */
  stages?: readonly CaptureFlowStageGroup[]
  /** A DÓNDE VA LA CONVERSACIÓN CUANDO LA CAPTURA TERMINA BIEN (F4-6).
   *
   *  Sale de las columnas del proceso (`terminalAfterCapture`), no de una
   *  preferencia suelta: si después de la ventana de captura hay una columna de
   *  `handoff` la conversación acaba con una persona; si hay una de `fin`, se
   *  despide; si no, el agente se queda contestando.
   *
   *  `'pasar'` (el valor por omisión) es el cierre de siempre, byte por byte:
   *  `confirmar` → `fin_traspaso`. Los otros dos mandan `confirmar` a `fin_ok`,
   *  que DEVUELVE EL TURNO al orquestador — `run-skill.ts` saca el marco de la
   *  pila y retoma en la arista de salida del `call` —, y es el orquestador
   *  quien despide o sigue atendiendo. Sin esto "termina con una despedida" era
   *  inalcanzable: el cierre siempre traspasaba y `fin.handoff` nunca regresa. */
  terminal?: AgentTerminal
}

/** Un tramo, como el EMISOR lo necesita: la etapa donde aterriza, el sufijo con
 *  el que se nombran sus nodos, y qué preguntas de `questions` hace. Los índices
 *  y no las preguntas: la lista plana es la misma que lee la vista previa. */
export interface CaptureFlowStageGroup {
  stage: string
  /** Sufijo de los ids `guardar_<idSuffix>` / `revisar_<idSuffix>`. El ÚLTIMO
   *  tramo no lo usa: sus nodos son `guardar` y `revisar`, que es el cierre
   *  bloqueado. */
  idSuffix: string
  questionIndexes: readonly number[]
  /** QUÉ CORRE EN ESTE TRAMO (D22-6). Ausente = `captura`, que es el único
   *  tramo que existía hasta aquí y lo que deja el emisor byte por byte igual
   *  para todo proceso sin etapa de agenda. */
  kind?: 'captura' | 'agenda'
  /** LA ETAPA QUE AGENDA (D22-6). Presente sólo en el tramo de `agenda`: después
   *  de preguntar lo suyo, el tramo busca disponibilidad REAL, ofrece el horario
   *  más cercano, reserva, y el `guardar` del cierre bloqueado se vuelve el
   *  puente que mueve la tarjeta con la cita (D22-5). */
  agenda?: CaptureFlowAgenda
}

/** Lo que el emisor necesita saber para AGENDAR en un tramo: con qué busca y qué
 *  de la visita ya preguntó el proceso. Las variables llegan RESUELTAS desde el
 *  plan (`planCaptureFlow`), que es quien lee la ficha: el emisor no adivina
 *  claves ni las deriva de un nombre. */
export interface CaptureFlowAgenda {
  /** La variable cuya respuesta es la ESPECIALIDAD que se busca. */
  specialtyVar: string
  /** La variable cuya respuesta es CUÁNDO la quiere. */
  whenVar: string
  /** Las variables de la toma de datos de la visita que ESTE proceso pregunta.
   *  Las que no pregunta van ausentes, y su argumento no viaja: mandar un
   *  `${motivo}` que nadie contestó escribiría la interpolación cruda en la
   *  cita. */
  intake?: { reason?: string; firstVisit?: string; insurer?: string }
}

/** EL PREFIJO DE TODO NODO DEL TRAMO DE RESERVA de una captura por etapas.
 *
 *  Se exporta — junto con su pareja de lectura — porque la fusión y el detector
 *  de deriva tienen que RECONOCER esos nodos: el tramo no es un paso que el
 *  cliente escribió, así que compararlo contra lo autorado reportaría como
 *  edición ajena lo que el compilador acaba de emitir. Una segunda derivación
 *  de la convención del otro lado sería la deriva de siempre.
 *
 *  NO CHOCA con ninguna familia de ids de la espina (`start`, `pedir_*`,
 *  `guardar*`, `revisar*`, `confirmar`, `fin_*`, `avisar_asesor`): ninguna
 *  empieza con `cita_`, y la de un dato llamado `cita_lo_que_sea` se llama
 *  `pedir_cita_lo_que_sea`. */
export const AGENDA_NODE_PREFIX = 'cita_'

export function isAgendaSegmentNodeId(id: string): boolean {
  return id.startsWith(AGENDA_NODE_PREFIX)
}

/** El nodo que dice la cita APARTADA (la reserva quedó pendiente de que el
 *  consultorio la confirme). Es el ÚNICO nodo que el cierre bloqueado gana. */
export const AGENDA_PENDING_NODE = `${AGENDA_NODE_PREFIX}pendiente`

/** Los ids del tramo de reserva de una captura por etapas, ENTEROS: el emisor
 *  del tramo no deriva ni prefija ni uno solo (D22-4), así que la convención se
 *  escribe aquí, una vez. */
function agendaSegmentIds(): BookingSegmentIds {
  const p = AGENDA_NODE_PREFIX
  return {
    buscar: `${p}buscar`,
    hay: `${p}hay`,
    exacto: `${p}exacto`,
    ofrecer: `${p}ofrecer`,
    decidirDirecto: `${p}decidir_directo`,
    elegirDirecto: `${p}elegir_directo`,
    aviso: `${p}aviso`,
    listar: `${p}listar`,
    elegirLista: `${p}elegir_lista`,
    reservar: `${p}reservar`,
    confirmar: `${p}confirmar`,
    ocupado: `${p}ocupado`,
    leadNoofrece: `${p}lead_noofrece`,
    noofrece: `${p}noofrece`,
    finNoofrece: `${p}fin_noofrece`,
    leadSin: `${p}lead_sin`,
    sin: `${p}sin`,
    finSin: `${p}fin_sin`,
  }
}

/** Las preguntas del flujo de fábrica de salud. Viven aquí, y no dentro del
 *  builder, para que la ruta por omisión y la generada usen el mismo armado.
 *
 *  Exportadas porque `FACTORY_ASK_LABELS` (abajo) le pone nombre humano a estas
 *  MISMAS variables: la prueba de al lado compara las dos listas para que
 *  agregar una pregunta de fábrica sin bautizarla no pase en silencio. */
export const FACTORY_CAPTURE_QUESTIONS: readonly CaptureFlowQuestion[] = [
  { var: 'nombre', prompt: '¿Con quién tengo el gusto?', extract: { kind: 'string' } },
  {
    var: 'especialidad',
    prompt: '¿Para qué especialidad buscas atención?',
    // F39. Esta respuesta aterriza en la COLUMNA del interés, que es lo que
    // pinta el chip de la tarjeta y lo que filtra el tablero por área, y el dato
    // del catálogo es una LISTA. Capturarla como texto libre guardaba
    // "me duele el pecho" como si fuera una especialidad: la tarjeta quedaba con
    // un área que ningún filtro encuentra y que el propio Select de la ficha no
    // puede volver a seleccionar. Como `enum`, el clasificador tiene que
    // resolver la respuesta a UNA de estas opciones o la pregunta se repite.
    extract: { kind: 'enum', values: [...HEALTHCARE_SPECIALTIES] },
    interest: true,
  },
  { var: 'ubicacion', prompt: '¿En qué zona te queda mejor?', extract: { kind: 'string' } },
]

/** El nombre HUMANO de cada variable que el flujo de captura DE FÁBRICA
 *  pregunta.
 *
 *  POR QUÉ EXISTE. El flujo sólo guarda la CLAVE (`ubicacion`); quien la lee en
 *  pantalla es un Dueño. Mientras el espacio tenga un dato de ficha o de
 *  biblioteca con esa clave, la etiqueta sale de ahí — la palabra que el cliente
 *  escribió gana siempre. Pero un espacio recién sembrado corre la captura de
 *  fábrica con la ficha de Prospectos VACÍA, y ahí no había etiqueta ninguna: la
 *  vista previa escribía la clave tal cual y el delta decía «se quitarán las
 *  preguntas de "especialidad" y "ubicacion"», en minúsculas y sin acentos, dos
 *  renglones arriba de "Presupuesto". Eso no se lee como un dato: se lee como
 *  que el producto está a medio hacer.
 *
 *  LAS PALABRAS NO SE INVENTAN AQUÍ. Son las que la plataforma ya sienta en la
 *  ficha del giro (`packages/engine/src/fields/field-presets.ts`): el preset
 *  administrado del interés de salud se llama "Especialidad" — que es donde
 *  aterriza la respuesta de `especialidad` (F26) — y el preset `ubicacion` se
 *  llama "Ubicación". Se copian y no se importan porque `@vysto/shared` no puede
 *  importar `@vysto/engine`, y la copia es de TRES palabras que el mismo
 *  arranque siembra. */
export const FACTORY_ASK_LABELS: Readonly<Record<string, string>> = {
  nombre: 'Nombre',
  especialidad: 'Especialidad',
  ubicacion: 'Ubicación',
}

/** Cómo se NOMBRA una pregunta de la que sólo se conoce la clave.
 *
 *  Es el ÚLTIMO recurso de la resolución de etiquetas de la vista previa (ficha
 *  → biblioteca → esto), y por eso vive en un solo lugar: el lado vivo y el
 *  lado compilado tienen que caer en la misma palabra o el delta empareja mal.
 *
 *  Dos pasos, en orden. Primero las de fábrica, con su nombre de catálogo.
 *  Después, cualquier otra clave: guiones bajos a espacios y mayúscula inicial,
 *  y NADA más. No se adivinan acentos: `direccion` se queda en "Direccion".
 *  Ponerle una tilde a la clave de alguien es escribir una palabra que el
 *  cliente nunca escribió, que es justo lo que esta función existe para dejar de
 *  hacer. */
export function humanizeAskVar(variable: string): string {
  const known = FACTORY_ASK_LABELS[variable]
  if (known !== undefined) return known
  const words = variable.replace(/[_-]+/g, ' ').trim()
  if (words === '') return variable
  return words.charAt(0).toLocaleUpperCase('es-MX') + words.slice(1)
}

const FACTORY_CAPTURE_CONFIRM =
  'Listo, ${nombre}. Ya guardé lo que necesitamos: ${especialidad} por la zona de ' +
  '${ubicacion}. Te paso con una persona del equipo para darle seguimiento.'

/** LA ÚLTIMA FRASE DE LA CONFIRMACIÓN, según a dónde va la conversación (F4-6).
 *
 *  La confirmación promete lo que pasa DESPUÉS, así que no puede ser la misma
 *  cuando después no pasa lo mismo: "Te paso con una persona" en un proceso que
 *  se despide es la promesa de un traspaso que nadie va a hacer. `cierre` y
 *  `fin_ok` comparten frase a propósito: desde el lado de la persona los dos
 *  significan "aquí sigo", y quien despide es el orquestador con SU texto. */
const CONFIRM_TAIL: Record<AgentTerminal, string> = {
  pasar: 'Te paso con una persona del equipo para darle seguimiento.',
  cierre: 'Si necesitas algo más, aquí estoy.',
  fin_ok: 'Si necesitas algo más, aquí estoy.',
}

/** La confirmación de un flujo GENERADO cuando NO hay ningún dato que leer de
 *  vuelta: la captura preguntó el nombre y nada más. Es el texto que este
 *  archivo publicaba para TODA captura generada hasta R4. */
export const GENERATED_CAPTURE_CONFIRM =
  'Listo, ${nombre}. Ya guardé tus datos. Te paso con una persona del equipo para ' +
  'darle seguimiento.'

/** CON QUÉ ABRE LA CONFIRMACIÓN (F4-7).
 *
 *  Con el nombre cuando la captura lo preguntó, y sin él cuando no. No es una
 *  preferencia de redacción: desde que la pregunta del nombre es removible —
 *  quitando su renglón de la ficha, F5-1; `asks_name = false` hasta F4-7 —, la
 *  variable `nombre` puede no existir en el flujo, y
 *  `renderText` resuelve una variable sin valor a la cadena vacía. Saludar por
 *  nombre a quien nunca lo dijo saldría como "Listo, ." en el WhatsApp del
 *  prospecto, que es peor que no saludarlo. La tarjeta SÍ queda con nombre (lo
 *  deriva `capturar_lead` del perfil del contacto), pero ese nombre vive en la
 *  tarjeta, no en las variables del flujo. */
const CONFIRM_OPENER = 'Listo'

function confirmHead(asksName: boolean): string {
  return asksName ? `${CONFIRM_OPENER}, \${nombre}. ` : `${CONFIRM_OPENER}. `
}

/** La misma frase sin datos que leer, para una captura que NO traspasa (F4-6),
 *  y desde F4-7 también para una que no pregunta el nombre. Se arma con la
 *  constante de arriba y no se copia entera: la mitad que no cambia tiene que
 *  seguir siendo una sola. */
function noEchoConfirm(terminal: AgentTerminal, asksName: boolean): string {
  return terminal === 'pasar' && asksName
    ? GENERATED_CAPTURE_CONFIRM
    : `${confirmHead(asksName)}Ya guardé tus datos. ${CONFIRM_TAIL[terminal]}`
}

/** Todas las frases sin datos que leer que NOSOTROS escribimos, para que la
 *  pareja de lectura las reconozca sin volver a escribirlas. */
const NO_ECHO_CONFIRMS: readonly string[] = (['pasar', 'cierre', 'fin_ok'] as const).flatMap(
  (t) => [noEchoConfirm(t, true), noEchoConfirm(t, false)],
)

/** R4 — la confirmación de un flujo GENERADO, LEYENDO DE VUELTA lo capturado.
 *
 *  El texto anterior decía "Ya guardé tus datos" y nada más. La frase es cierta
 *  y es inútil: nadie puede corregir un dato que nunca escuchó. R1 (el nombre
 *  del proceso guardado como zona del prospecto) sobrevivió en una conversación
 *  entera precisamente porque la confirmación no lo dijo en voz alta — la
 *  persona vio "Ya guardé tus datos", contestó "gracias", y la tarjeta quedó
 *  mal. Una confirmación que no se puede desmentir no es una confirmación.
 *
 *  El flujo de FÁBRICA (`FACTORY_CAPTURE_CONFIRM`) siempre hizo esto: nombra
 *  "Cardiología por la zona de Polanco". Lo que no podía hacer un flujo generado
 *  era escribir esa gramática, porque no sabe qué datos lleva la ficha. Lo que
 *  sí puede es nombrarlos: "Etiqueta: valor", en el orden en que se
 *  preguntaron. Feo de leer en un documento; exacto de leer en un WhatsApp.
 *
 *  `labels` viene de la ficha (el plan ya las trae, alineadas con `questions`),
 *  tal cual las escribió el cliente en Datos del proceso: la palabra que ve en
 *  el tablero es la palabra que el agente le dice. `nombre` NO se repite en la
 *  lista — ya está en el "Listo, ${nombre}". Sin datos que leer, devuelve la
 *  constante de arriba: no hay lista que enseñar.
 *
 *  LA ÚLTIMA FRASE SIGUE A LA TERMINAL (F4-6). Hasta aquí era siempre la del
 *  traspaso, porque el cierre generado siempre traspasaba. Ahora el proceso dice
 *  a dónde va la conversación, y prometer un traspaso que no va a ocurrir es la
 *  misma clase de mentira que R4 vino a quitar. Sin argumento se queda en
 *  `'pasar'`, que es la frase de siempre, byte por byte. */
export function generatedCaptureConfirm(
  echoed: readonly { variable: string; label: string }[],
  terminal: AgentTerminal = 'pasar',
  /** ¿La captura PREGUNTÓ el nombre? (F4-7). Por omisión sí, que es el texto de
   *  siempre byte por byte. En `false` la confirmación abre sin nombre, porque
   *  la variable no existe y `renderText` la dejaría en blanco. */
  asksName = true,
): string {
  const parts = echoed
    .filter((e) => e.variable !== 'nombre' && e.label.trim() !== '')
    // `'${' + variable + '}'` y no un template literal: el texto que sale lleva
    // la interpolación del RUNTIME (`renderText`), no la de TypeScript.
    .map((e) => e.label.trim() + ': ${' + e.variable + '}')
  if (parts.length === 0) return noEchoConfirm(terminal, asksName)
  return confirmHead(asksName) + 'Guardé: ' + parts.join(', ') + '. ' + CONFIRM_TAIL[terminal]
}

/** LAS DOS MANERAS EN QUE UNA RESERVA QUEDA, dichas por el flujo (D22-6).
 *
 *  `firme` es la cita agendada; `apartada` es la que quedó esperando a que el
 *  consultorio la confirme. Las dos se dicen DESPUÉS de reservar, así que las
 *  dos son ciertas en su rama aunque la tarjeta no se haya podido escribir: lo
 *  que se promete es el HORARIO, que ya se comprometió en la agenda. */
export const AGENDA_CONFIRM_KINDS = ['firme', 'apartada'] as const
export type AgendaConfirmKind = (typeof AGENDA_CONFIRM_KINDS)[number]

/** El cuerpo de cada una. Nombra el horario, al doctor y el FOLIO, igual que la
 *  confirmación de la rama de fábrica: un número de confirmación es lo único
 *  que la persona puede repetir después para que alguien encuentre su cita. */
const AGENDA_CONFIRM_BODY: Record<AgendaConfirmKind, string> = {
  firme:
    'Tu cita quedó agendada para ${elegido.starts_at_label} con ${elegido.doctor_name}. ' +
    'Tu número de confirmación es ${reserva.booking_id}.',
  apartada:
    'Te aparté ${elegido.starts_at_label} con ${elegido.doctor_name}. Queda pendiente de que ' +
    'el consultorio la confirme; te aviso por aquí en cuanto esté lista. ' +
    'Tu número de confirmación es ${reserva.booking_id}.',
}

/** D22-6 — la confirmación de una captura que AGENDÓ.
 *
 *  Es la hermana de `generatedCaptureConfirm` y se arma con las mismas piezas
 *  (la apertura con o sin nombre, la última frase que sigue a la terminal), así
 *  que las dos familias no pueden separarse en redacción ni en forma. Lo que
 *  cambia es el medio: una captura lee de vuelta lo capturado, y una reserva
 *  dice la cita — que es el dato que la persona necesita revisar. */
export function generatedAgendaConfirm(
  kind: AgendaConfirmKind,
  terminal: AgentTerminal = 'pasar',
  /** ¿La captura PREGUNTÓ el nombre? Sin él la confirmación abre sin saludo,
   *  porque la variable no existe y `renderText` la dejaría en blanco. */
  asksName = true,
): string {
  return `${confirmHead(asksName)}${AGENDA_CONFIRM_BODY[kind]} ${CONFIRM_TAIL[terminal]}`
}

/** ¿Este texto lo escribimos NOSOTROS o lo escribió el cliente?
 *
 *  La pareja de lectura de `generatedCaptureConfirm`, y hace falta porque la
 *  confirmación generada ya no es UNA constante: depende de la ficha, así que
 *  comparar contra el texto del plan de HOY marca como "editado por el cliente"
 *  cualquier flujo cuya ficha cambió desde que se generó. Lo que se reconoce es
 *  la FORMA, que es lo que de verdad distingue nuestro texto del suyo.
 *
 *  Incluye la constante anterior a R4: los flujos que ya viven en la base la
 *  traen, y también la escribimos nosotros.
 *
 *  Y desde F4-6 son DOS formas, no una: la última frase sigue a la terminal del
 *  proceso. Reconocer sólo la del traspaso haría que toda captura de un proceso
 *  que se despide reportara una edición del cliente que nadie hizo, y la fusión
 *  conservaría para siempre un texto que nosotros escribimos. */
export function isGeneratedCaptureConfirm(text: string): boolean {
  const t = text.trim()
  if (NO_ECHO_CONFIRMS.includes(t)) return true
  // DOS FAMILIAS desde D22-6: la captura que lee de vuelta lo capturado, y la
  // reserva que dice la cita. Un proceso con etapa de `agenda` publica la
  // segunda, y reconocer sólo la primera haría que TODO flujo suyo reportara
  // para siempre una edición del cliente que nadie hizo.
  return GENERATED_CONFIRM_SHAPE.test(t) || GENERATED_AGENDA_CONFIRM_SHAPE.test(t)
}

/** Lo que hay que escapar para meter una frase nuestra dentro de un patrón. */
function escapeForRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** La FORMA de la confirmación generada, con sus dos finales y sus dos
 *  aperturas. Se arma con las mismas frases que `generatedCaptureConfirm`
 *  escribe: una copia literal aquí sería la pareja de lectura separándose de la
 *  de escritura al primer ajuste de redacción.
 *
 *  LA APERTURA SIN NOMBRE ES F4-7: un proceso que no pregunta el nombre publica
 *  "Listo. Guardé: …", y reconocer sólo la forma con nombre haría que TODA
 *  captura suya reportara una edición del cliente que nadie hizo — y que la
 *  fusión conservara para siempre un texto que nosotros escribimos. */
const GENERATED_CONFIRM_SHAPE = new RegExp(
  '^' +
    escapeForRegExp(CONFIRM_OPENER) +
    '(?:, \\$\\{nombre\\})?\\. Guardé: .+\\. (?:' +
    [...new Set(Object.values(CONFIRM_TAIL))].map(escapeForRegExp).join('|') +
    ')$',
)

/** La FORMA de la confirmación de una RESERVA (D22-6). Se arma con los MISMOS
 *  cuerpos que `generatedAgendaConfirm` escribe — nunca con una copia literal —,
 *  y con las mismas aperturas y finales que la de la captura. Lo único variable
 *  es la interpolación del runtime, que va literal dentro del cuerpo. */
const GENERATED_AGENDA_CONFIRM_SHAPE = new RegExp(
  '^' +
    escapeForRegExp(CONFIRM_OPENER) +
    '(?:, \\$\\{nombre\\})?\\. (?:' +
    Object.values(AGENDA_CONFIRM_BODY).map(escapeForRegExp).join('|') +
    ') (?:' +
    [...new Set(Object.values(CONFIRM_TAIL))].map(escapeForRegExp).join('|') +
    ')$',
)

/** El id del nodo de una pregunta. La primera es `start` (el validador pide un
 *  nodo con ese id); las demás llevan el nombre de su variable. */
function captureAskId(index: number, variable: string): string {
  return index === 0 ? 'start' : `pedir_${variable}`
}

/** The salud/doctor starter capture flow: nombre → especialidad → zona →
 *  `capturar_lead` → confirma y PASA la conversación con la persona a la que
 *  quedó asignado el prospecto. `especialidad` viaja como `interes` (el interés
 *  vive en la columna `specialty_label`, que es lo que la ficha muestra como
 *  "Especialidad") y la zona como un campo de la ficha.
 *
 *  POR QUÉ EL ÉXITO TRASPASA — Y DESDE CUÁNDO NO SIEMPRE.
 *
 *  Lock de Juan, 2026-08-22: una captura que aterriza en una etapa que una
 *  persona atiende le entrega la TARJETA a esa persona; entregarle además la
 *  CONVERSACIÓN es lo que hace que el prospecto no quede esperando a un bot que
 *  ya terminó su trabajo. Es una decisión de AUTORÍA, no del runtime: el
 *  traspaso está escrito en el flujo y quien prefiera que el bot siga atendiendo
 *  cambia esa terminal por un `fin.ok`. El traspaso usa `assign_to: auto`, así
 *  que la cadena de `ops/handoff-router.ts` lo deja en el peldaño 2
 *  (`lead_assignee`): la persona que la asignación de entrada acaba de elegir
 *  para esta tarjeta.
 *
 *  Aprobación de Juan del 2026-09-12, punto 8 ("el final como control visible"),
 *  que AMPLÍA el lock sin desdecirlo: quién cambia esa terminal ya no es quien
 *  abre el editor, es el PROCESO. El cierre sigue traspasando siempre que el
 *  proceso termine con una persona (`terminal: 'pasar'`, el valor por omisión y
 *  el flujo de fábrica byte por byte); cuando el proceso se despide o el agente
 *  se queda contestando, el éxito va a `fin.ok` y DEVUELVE EL TURNO al
 *  orquestador, que es quien despide. Hasta este cambio "termina con una
 *  despedida" era inalcanzable: la vista previa la prometía y el runtime no
 *  podía producirla, porque `fin.handoff` nunca saca el marco de la pila.
 *
 *  `revisar` tiene tres salidas reales, y todas terminan en algún lado:
 *    - se creó la tarjeta → `confirmar` → `fin_traspaso` (fin.handoff, auto) con
 *      `terminal: 'pasar'`, o → `fin_ok` con las otras dos;
 *    - `capturar_lead` rechazó (la etapa pide datos que el flujo no juntó) →
 *      `avisar_asesor` → `fin_handoff`;
 *    - cualquier otro caso → `fin_ok`, el bot sigue atendiendo sin traspasar.
 *  La tercera es la que mantiene honesta la invariante: `fin.ok` existe Y es
 *  alcanzable desde el inicio, que es lo que revisa el validador (regla 2 pide
 *  que exista, regla 3 rechaza cualquier nodo huérfano).
 *
 *  Y ES ESA REGLA 3 la que decide la única diferencia de NODOS entre las dos
 *  formas: sin la arista `confirmar` → `fin_traspaso`, ese nodo se queda sin
 *  entradas, el validador lo reporta como huérfano con severidad `error` y la
 *  generación se rechazaría entera. Así que un cierre que no traspasa no lo
 *  emite. Los demás seis siguen ahí, con las mismas tres salidas de `revisar`
 *  (`endingIsStandard` reconoce las dos formas).
 *
 *  Nunca un final en silencio: desde el arreglo de `handoffTerminal`
 *  (run-skill.ts) una terminal de traspaso ENTREGA lo que el flujo compuso en
 *  el turno, así que la confirmación y el aviso se escuchan antes de la frase
 *  genérica de traspaso.
 *
 *  Con `options` el MISMO armado genera el flujo de captura de cualquier
 *  proceso desde su ficha (C1): cambian las preguntas, el embudo, la etapa y la
 *  confirmación; el cierre de tres ramas NO se toca, porque su semántica está
 *  fijada por el lock de Juan y por lo que revisa el validador. Sin `options`
 *  devuelve el flujo de fábrica tal cual, byte por byte: el seeder de demo lo
 *  duplica como JSON y `starter-capture-flow.test.ts` compara las dos casas. */
export function buildCaptureFlow(options: CaptureFlowOptions = {}): StarterFlow {
  const XL = 20 // la vía "el bot sigue", a la izquierda
  const X = 360
  const XR = 700 // la vía del traspaso, a la derecha
  const y = (row: number): number => 40 + row * 130

  const questions = options.questions ?? FACTORY_CAPTURE_QUESTIONS
  const confirmText = options.confirmText ?? FACTORY_CAPTURE_CONFIRM
  const funnel = options.funnel ?? CAPTURE_FLOW_FUNNEL
  const stage = options.stage ?? CAPTURE_FLOW_STAGE
  const terminal = options.terminal ?? 'pasar'
  if (questions.length === 0) {
    throw new Error('buildCaptureFlow: un flujo de captura necesita al menos una pregunta.')
  }
  // CUÁL PREGUNTA ES EL NOMBRE (F4-7). Ausente cae a la posicional de siempre,
  // que es lo que deja el flujo de fábrica byte por byte; `null` dice que no hay
  // ninguna, y ese estado no se puede confundir con "no lo mandaste".
  const nameVar = options.nameVar === undefined ? questions[0]!.var : options.nameVar
  // POR ETAPAS (F3-1b) es un emisor APARTE, no una rama dentro de éste: el
  // camino de siempre tiene que seguir devolviendo el mismo flujo byte por byte,
  // y la manera de garantizarlo es no tocar ni una línea de su armado.
  if (options.stages !== undefined) {
    return buildStagedCaptureFlow(questions, confirmText, funnel, options.stages, terminal, nameVar)
  }

  // Las preguntas, una por dato, encadenadas de arriba abajo.
  const askNodes: StarterNode[] = questions.map((q, i) => ({
    id: captureAskId(i, q.var),
    type: 'ask',
    position: { x: X, y: y(i) },
    // `preserved` va al FINAL a propósito: cuando la fusión conserva un
    // `max_retries` autorado, ese gana sobre el 2 de fábrica.
    config: { prompt: q.prompt, var: q.var, extract: q.extract, max_retries: 2, ...q.preserved },
  }))

  // Los argumentos de `capturar_lead`: el nombre y el interés son columnas del
  // prospecto y viajan sueltos; todo lo demás es la ficha y va en `campos`.
  const interest = questions.find((q) => q.interest)
  const campos: Record<string, string> = {}
  for (const q of questions) {
    if (q.interest || q.var === nameVar) continue
    campos[q.var] = `\${${q.var}}`
  }
  const guardarArgs: Record<string, unknown> = {
    // Sin pregunta de nombre el argumento NO viaja: `capturar_lead` lo deriva
    // del perfil del contacto (F3-1a), y mandar un `${nombre}` que nadie
    // contestó escribiría la interpolación cruda en la tarjeta.
    ...(nameVar === null ? {} : { nombre: `\${${nameVar}}` }),
    ...(interest ? { interes: `\${${interest.var}}` } : {}),
    campos,
    embudo: funnel,
    etapa: stage,
  }

  // Las filas del cierre, contadas desde la última pregunta.
  const rGuardar = questions.length
  const rRevisar = rGuardar + 1
  const rSalida = rGuardar + 2 // confirmar / fin_ok / avisar_asesor
  const rFinal = rGuardar + 3 // fin_traspaso / fin_handoff

  return {
    nodes: [
      ...askNodes,
      {
        id: 'guardar',
        type: 'act',
        position: { x: X, y: y(rGuardar) },
        config: { tool: 'capturar_lead', args: guardarArgs, out: 'lead' },
      },
      { id: 'revisar', type: 'dec', position: { x: X, y: y(rRevisar) }, config: {} },
      {
        id: 'confirmar',
        type: 'msg',
        position: { x: X, y: y(rSalida) },
        config: { text: confirmText },
      },
      // Sólo cuando el éxito TRASPASA: sin su arista de entrada este nodo es
      // huérfano y el validador rechaza el flujo entero (regla 3).
      ...(terminal === 'pasar'
        ? [
            {
              id: 'fin_traspaso',
              type: 'fin',
              position: { x: X, y: y(rFinal) },
              config: {
                kind: 'handoff',
                reason: 'lead_capturado',
                // `auto`, no un asesor con nombre: la cadena resuelve al responsable
                // de la tarjeta recién creada (peldaño 2), así que el traspaso sigue
                // siendo correcto cuando esa persona cambia.
                assign_to: { kind: 'auto' },
              },
            } satisfies StarterNode,
          ]
        : []),
      { id: 'fin_ok', type: 'fin', position: { x: XL, y: y(rSalida) }, config: { kind: 'ok' } },
      {
        id: 'avisar_asesor',
        type: 'msg',
        position: { x: XR, y: y(rSalida) },
        config: {
          text: 'Déjame pasarte con una persona del equipo para terminar de registrar tus datos.',
        },
      },
      {
        id: 'fin_handoff',
        type: 'fin',
        position: { x: XR, y: y(rFinal) },
        config: { kind: 'handoff', reason: 'No se pudo registrar el prospecto' },
      },
    ],
    edges: [
      ...askNodes.map((n, i) => ({
        from: n.id,
        to: askNodes[i + 1]?.id ?? 'guardar',
      })),
      { from: 'guardar', to: 'revisar' },
      // `lead_id` vacío = la captura fue rechazada y no se escribió nada.
      { from: 'revisar', to: 'avisar_asesor', when: 'lead.lead_id.length == 0' },
      // Se creó la tarjeta: confirma y pasa la conversación con su responsable.
      { from: 'revisar', to: 'confirmar', when: 'lead.lead_id.length > 0' },
      // Salida por omisión (la herramienta no devolvió lo que esperábamos): el
      // bot sigue atendiendo en vez de traspasar a ciegas. Va al final porque el
      // runtime toma la PRIMERA arista que cumple.
      { from: 'revisar', to: 'fin_ok', when: 'true' },
      // A dónde va el ÉXITO. `fin_ok` saca el marco de la pila y le devuelve el
      // turno al orquestador, que despide o sigue atendiendo según su arista.
      { from: 'confirmar', to: terminal === 'pasar' ? 'fin_traspaso' : 'fin_ok' },
      { from: 'avisar_asesor', to: 'fin_handoff' },
    ],
  }
}

/** El emisor POR ETAPAS (F3-1b): la captura de un proceso que reparte sus datos
 *  entre varias columnas.
 *
 *  LA TARJETA NACE AL ENTRAR (lock de Juan). El primer nodo ya no es una
 *  pregunta: es un `capturar_lead` sin datos, en la primera etapa del tramo
 *  inicial. Antes, la tarjeta aparecía hasta el final — una conversación
 *  abandonada a la mitad no dejaba rastro y nadie podía darle seguimiento. El
 *  `nombre` NO viaja en ese primer llamado: la herramienta lo deriva del perfil
 *  del contacto (F3-1a), y mandar un `${nombre}` que todavía nadie contestó
 *  habría escrito la interpolación cruda en la tarjeta.
 *
 *  Después, un TRAMO por columna: sus preguntas, su `capturar_lead` con lo que
 *  esa columna obtiene, y su revisión. Un tramo sin preguntas sólo adelanta la
 *  tarjeta. La revisión de cada tramo mira `datos_faltantes` y no `lead_id`:
 *  con una tarjeta ya creada, `lead_id` viene lleno siempre y esa arista sería
 *  ciega. En un resultado exitoso `datos_faltantes` ni siquiera está, y el
 *  encadenamiento opcional de filtrex (expr.ts) lo resuelve a falso sin tronar.
 *
 *  EL CIERRE NO SE REDISEÑA: los siete nodos del lock de Juan (`guardar`,
 *  `revisar`, `confirmar`, `fin_traspaso`, `fin_ok`, `avisar_asesor`,
 *  `fin_handoff`) están todos, con las mismas tres salidas de `revisar`, así que
 *  `endingIsStandard` sigue reconociendo el flujo como nuestro.
 *
 *  Y EL TRAMO QUE AGENDA (D22-6) ES ADITIVO. Un proceso sin columna de `agenda`
 *  sale de aquí byte por byte como salía. Con ella, el tramo de reserva se
 *  intercala entre las preguntas de esa columna y su `guardar` — que deja de ser
 *  sólo un avance y se vuelve el PUENTE de D22-5: mueve la tarjeta a la columna
 *  que agendó, con la cita —, y el cierre gana UN nodo, el de la cita apartada,
 *  sin perder ninguna de sus tres salidas. */
function buildStagedCaptureFlow(
  questions: readonly CaptureFlowQuestion[],
  confirmText: string,
  funnel: string,
  groups: readonly CaptureFlowStageGroup[],
  /** A dónde va el éxito (F4-6). La ÚNICA diferencia entre las dos formas del
   *  cierre es a dónde manda `confirmar`, y con ella el `fin_traspaso` que se
   *  quedaría sin entrada. */
  terminal: AgentTerminal,
  /** Cuál pregunta es el nombre, o `null` cuando el proceso no lo pregunta
   *  (F4-7). Llega YA RESUELTA desde `buildCaptureFlow`: leer `questions[0]`
   *  aquí sería el emisor decidiendo por posición lo que la configuración dice
   *  por nombre, y la primera pregunta de una captura sin nombre es un dato de
   *  la ficha que se perdería. */
  nameVar: string | null,
): StarterFlow {
  if (groups.length === 0) {
    throw new Error('buildCaptureFlow: un flujo de captura por etapas necesita al menos un tramo.')
  }
  const XL = 20 // la vía "el bot sigue", a la izquierda
  const X = 360
  const XR = 700 // la vía del traspaso, a la derecha
  const XA = 1040 // el carril del tramo de reserva (D22-6), a la derecha de todo
  const COL = 260
  const ROW = 130
  const y = (row: number): number => 40 + row * ROW

  /** LO QUE YA SE PREGUNTÓ cuando un tramo guarda (F5-1).
   *
   *  El nombre y el interés viajan en argumentos SUELTOS de `capturar_lead`, y
   *  hasta aquí se armaban una vez y se mandaban en TODOS los tramos. Mientras
   *  las dos preguntas cayeron siempre en el primero eso dio igual; desde que el
   *  nombre se puede declarar en la segunda columna, un tramo anterior mandaría
   *  `nombre: "${nombre}"` sin que nadie lo hubiera contestado, y el runtime
   *  NO resuelve una variable sin valor a vacío en este camino: escribe la
   *  interpolación cruda. La tarjeta quedaría literalmente llamada "${nombre}".
   *
   *  Es la MISMA razón por la que el `act` de entrada nunca llevó el argumento
   *  (F3-1a), dicha para los tramos de en medio. El interés viaja en el mismo
   *  saco por el mismo motivo: también es declarable por columna. */
  const askedSoFar = new Set<string>()

  /** La espina dorsal, en el orden en que la conversación la recorre. El papel
   *  de cada nodo es lo único que decide sus aristas, así que las salidas se
   *  derivan de esta lista y no de un segundo recorrido de los tramos. */
  const spine: { node: StarterNode; role: 'ask' | 'act' | 'dec'; next?: string }[] = []
  const push = (role: 'ask' | 'act' | 'dec', node: Omit<StarterNode, 'position'>): void => {
    spine.push({
      node: { ...node, position: { x: X, y: y(spine.length) } },
      role,
    })
  }

  /** EL TRAMO DE RESERVA de la etapa de `agenda`, cuando el proceso la trae, y
   *  después de qué paso de la espina van sus nodos EN EL ARREGLO. El detector
   *  de deriva recorre arreglos y no el grafo, así que dónde caen importa: el
   *  `guardar` puente tiene que quedar DESPUÉS de todo, para que la secuencia de
   *  etapas que se lee del flujo sea la del proceso. */
  // En un objeto y no en dos `let`: se asignan dentro del recorrido de los
  // tramos, y el análisis de flujo de TypeScript da por no corrido un callback.
  const booking: { segment: BookingSegment | null; after: number } = { segment: null, after: -1 }
  /** ¿El cierre bloqueado decide por la RESERVA? Lo decide el último tramo. */
  let closesOnBooking = false

  groups.forEach((group, i) => {
    const isLast = i === groups.length - 1
    if (i === 0) {
      push('act', {
        id: 'start',
        type: 'act',
        config: {
          tool: 'capturar_lead',
          args: { embudo: funnel, etapa: group.stage },
          out: 'lead',
        },
      })
      push('dec', { id: 'revisar_entrada', type: 'dec', config: {} })
      // El par de entrada YA es el `act` + `dec` de este tramo cuando no
      // pregunta nada: un segundo `capturar_lead` a la misma etapa, con los
      // mismos datos, no adelantaría nada. Un tramo de `agenda` nunca cae aquí:
      // aunque no pregunte nada, lo suyo es RESERVAR.
      if (group.questionIndexes.length === 0 && !isLast && group.agenda === undefined) return
    }
    const asked = group.questionIndexes.map((idx) => questions[idx]!)
    for (const q of asked) askedSoFar.add(q.var)
    for (const q of asked) {
      push('ask', {
        id: `pedir_${q.var}`,
        type: 'ask',
        // `preserved` va al FINAL a propósito: cuando la fusión conserva un
        // `max_retries` autorado, ese gana sobre el 2 de fábrica.
        config: {
          prompt: q.prompt,
          var: q.var,
          extract: q.extract,
          max_retries: 2,
          ...q.preserved,
        },
      })
    }
    // Lo que ESTE tramo aprendió. El nombre y el interés son columnas del
    // prospecto y viajan sueltos; todo lo demás es la ficha y va en `campos`.
    const campos: Record<string, string> = {}
    for (const q of asked) {
      if (q.interest || q.var === nameVar) continue
      campos[q.var] = `\${${q.var}}`
    }
    // Los dos sueltos, sólo si ya se preguntaron: ver `askedSoFar`.
    const interest = questions.find((q) => q.interest && askedSoFar.has(q.var))
    const nombreArg =
      nameVar !== null && askedSoFar.has(nameVar) ? { nombre: `\${${nameVar}}` } : {}
    const interesArg = interest ? { interes: `\${${interest.var}}` } : {}

    // ── LA ETAPA QUE AGENDA (D22-6) ──────────────────────────────────────────
    //
    // El tramo ya preguntó lo que su columna declara, arriba, como cualquier
    // otro. Lo que sigue no es guardar: es BUSCAR HORARIO DE VERDAD, ofrecer el
    // más cercano, reservar, y sólo entonces escribir la tarjeta — con la cita.
    //
    // El `guardar` del cierre bloqueado SE VUELVE EL PUENTE (D22-5): lleva la
    // tarjeta a ESTA columna con `cita`, así que el tablero, la línea de tiempo
    // y los reportes dicen lo mismo que cuando la mueve el botón del cajón.
    if (group.agenda) {
      const agenda = group.agenda
      const ids = agendaSegmentIds()
      // Lo que la conversación YA sabe, para las dos salidas por las que el
      // tramo se rinde: quien pidió una cita y dio una fecha es un prospecto
      // caliente aunque la cita no se haya podido agendar (F6-5). Es TODO lo
      // preguntado, no lo de este tramo: las preguntas de la columna que agenda
      // todavía no se guardaron en ningún lado.
      const todoLoPreguntado: Record<string, string> = {}
      for (const q of questions) {
        if (!askedSoFar.has(q.var) || q.interest || q.var === nameVar) continue
        todoLoPreguntado[q.var] = `\${${q.var}}`
      }
      const segment = buildBookingSegment({
        ids,
        specialtyExpr: `\${${agenda.specialtyVar}}`,
        // La fecha viaja YA INTERPRETADA: el dato es de tipo `date`, así que su
        // `ask` la extrae como instante ISO y el camino rápido de D22-2 la lee
        // sin volver a adivinar el idioma.
        whenExpr: `\${${agenda.whenVar}}`,
        bookArgs: {
          patient_name: '${contact.profile_name}',
          phone: '${contact.phone}',
          // Sólo lo que ESTE proceso pregunta: un `${motivo}` que nadie contestó
          // escribiría la interpolación cruda en la cita.
          ...(agenda.intake?.reason ? { reason: `\${${agenda.intake.reason}}` } : {}),
          ...(agenda.intake?.firstVisit ? { first_visit: `\${${agenda.intake.firstVisit}}` } : {}),
          ...(agenda.intake?.insurer ? { insurer: `\${${agenda.intake.insurer}}` } : {}),
        },
        // SIN `etapa` y SIN `cita`: la tarjeta se queda donde la captura la
        // dejó. Adelantarla a la columna que agenda cuando NO se agendó sería
        // el tablero mintiendo, y el recorrido del prospecto dejaría de leerse.
        leadArgs: { ...interesArg, campos: todoLoPreguntado, embudo: funnel },
        copy: DEFAULT_BOOKING_COPY,
        layout: { x: XA, y: 40, col: COL, row: ROW },
        // La toma de datos de la visita ya la hizo la espina, arriba: aquí el
        // horario elegido entra DIRECTO a reservar.
        intake: { nodes: [], edges: [], entryId: ids.reservar, exitId: ids.reservar },
        // Y la reserva hecha vuelve al cierre bloqueado, por sus dos ramas: el
        // puente es UNO solo, y quién dice qué lo decide `revisar` abajo.
        booked: { nodes: [], edges: [], confirmedId: 'guardar', pendingId: 'guardar' },
      })
      // `confirmedId` y `pendingId` apuntan los DOS al puente, así que el tramo
      // escribió dos aristas idénticas desde su decisión. Se queda la de por
      // omisión: cuál de las dos frases se dice lo decide `revisar`, abajo, con
      // el mismo estado, y dos aristas iguales sólo confunden a quien lea el
      // grafo.
      segment.edges = segment.edges.filter(
        (e) => !(e.from === ids.confirmar && e.to === 'guardar' && e.when !== 'true'),
      )
      // El paso anterior entra al tramo en vez de caer en `guardar`.
      spine[spine.length - 1]!.next = segment.entryId
      booking.segment = segment
      booking.after = spine.length - 1
      closesOnBooking = true
      push('act', {
        id: 'guardar',
        type: 'act',
        config: {
          tool: 'capturar_lead',
          args: {
            ...nombreArg,
            ...interesArg,
            campos,
            // EL PUENTE (D22-5): el instante del horario que se acaba de tomar,
            // tal cual lo devuelve `select_slot` (ya es ISO en UTC).
            cita: '${elegido.starts_at}',
            embudo: funnel,
            etapa: group.stage,
          },
          out: 'lead',
        },
      })
      push('dec', { id: 'revisar', type: 'dec', config: {} })
      return
    }

    push('act', {
      id: isLast ? 'guardar' : `guardar_${group.idSuffix}`,
      type: 'act',
      config: {
        tool: 'capturar_lead',
        args: {
          // Sin pregunta de nombre — o antes de que se haya hecho — el argumento
          // no viaja: la tarjeta ya nació al entrar y `capturar_lead` la bautizó
          // con el perfil del contacto. El tramo que lo aprende la rebautiza.
          ...nombreArg,
          ...interesArg,
          campos,
          embudo: funnel,
          etapa: group.stage,
        },
        out: 'lead',
      },
    })
    push('dec', { id: isLast ? 'revisar' : `revisar_${group.idSuffix}`, type: 'dec', config: {} })
  })

  const rSalida = spine.length // confirmar / fin_ok / avisar_asesor
  const rFinal = spine.length + 1 // fin_traspaso / fin_handoff

  const edges: StarterFlow['edges'] = []
  spine.forEach((step, i) => {
    // A dónde SIGUE la conversación: el paso de al lado, salvo que el tramo lo
    // desvíe (la última pregunta de la etapa que agenda entra al tramo de
    // reserva, y la reserva vuelve sola al `guardar` puente).
    const nextId = step.next ?? spine[i + 1]?.node.id
    if (step.role !== 'dec') {
      edges.push({ from: step.node.id, to: nextId! })
      return
    }
    // `datos_faltantes` = la etapa pide datos que el flujo no juntó y no se
    // escribió el avance: una persona termina de registrarlos.
    //
    // SIN `.length > 0`, y no es un descuido. En un resultado exitoso el campo
    // NI SIQUIERA VIENE (es opcional en la salida de `capturar_lead`), y filtrex
    // no truena con `undefined > 0`: DEVUELVE un TypeError como valor, que el
    // runtime lee como verdadero (`Boolean(err)`), así que esa arista se habría
    // tomado en TODA captura exitosa. La referencia pelada resuelve a `undefined`
    // (falso) cuando no hay rechazo y al arreglo de etiquetas (verdadero) cuando
    // sí, que es exactamente la pregunta. Lo prueba expr.test.ts.
    edges.push({ from: step.node.id, to: 'avisar_asesor', when: 'lead.datos_faltantes' })
    if (nextId !== undefined) {
      edges.push({ from: step.node.id, to: nextId, when: 'true' })
      return
    }
    // EL CIERRE BLOQUEADO, en sus dos lecturas. Sigue teniendo EXACTAMENTE tres
    // salidas (lo que `endingIsStandard` revisa), y la primera es la misma
    // siempre: la etapa pidió datos que el flujo no juntó → una persona.
    if (closesOnBooking) {
      // Con una cita de por medio, lo que decide qué se dice es la RESERVA y no
      // la tarjeta: la cita ya se comprometió en la agenda aunque el CRM haya
      // fallado, así que las dos frases son ciertas en su rama.
      edges.push({
        from: step.node.id,
        to: 'confirmar',
        when: `reserva.status == "${BOOKING_CONFIRMED_STATUS}"`,
      })
      edges.push({ from: step.node.id, to: AGENDA_PENDING_NODE, when: 'true' })
      return
    }
    // Se creó la tarjeta → confirma y pasa la conversación con su responsable;
    // cualquier otro caso → el bot sigue atendiendo en vez de traspasar a
    // ciegas. La salida por omisión va al final porque el runtime toma la
    // PRIMERA arista que cumple.
    edges.push({ from: step.node.id, to: 'confirmar', when: 'lead.lead_id.length > 0' })
    edges.push({ from: step.node.id, to: 'fin_ok', when: 'true' })
  })
  // Las del tramo de reserva, en el orden en que su emisor las escribe.
  if (booking.segment !== null) edges.push(...booking.segment.edges)
  // A dónde va el ÉXITO. `fin_ok` saca el marco de la pila y le devuelve el
  // turno al orquestador, que despide o sigue atendiendo según su arista.
  edges.push({ from: 'confirmar', to: terminal === 'pasar' ? 'fin_traspaso' : 'fin_ok' })
  // LA CITA APARTADA DEVUELVE EL TURNO. `fin_ok` saca el marco de la pila y el
  // orquestador sigue atendiendo, que es lo mismo que hace la rama de reserva de
  // fábrica con su mensaje de "quedó pendiente". Y es lo que mantiene ALCANZABLE
  // el `fin_ok` del cierre bloqueado cuando el proceso traspasa: la arista por
  // omisión de `revisar` ya no llega ahí, y un `fin.ok` huérfano haría que el
  // validador rechazara el flujo entero (regla 3).
  if (closesOnBooking) edges.push({ from: AGENDA_PENDING_NODE, to: 'fin_ok' })
  edges.push({ from: 'avisar_asesor', to: 'fin_handoff' })

  const spineNodes = spine.map((s) => s.node)

  return {
    nodes: [
      // Los nodos del tramo de reserva van INTERCALADOS donde la conversación
      // los recorre: después de las preguntas de la etapa que agenda y antes del
      // `guardar` puente, para que la secuencia de etapas que el detector de
      // deriva lee del arreglo sea exactamente la del proceso.
      ...(booking.segment === null
        ? spineNodes
        : [
            ...spineNodes.slice(0, booking.after + 1),
            ...booking.segment.nodes,
            ...spineNodes.slice(booking.after + 1),
          ]),
      {
        id: 'confirmar',
        type: 'msg',
        position: { x: X, y: y(rSalida) },
        config: { text: confirmText },
      },
      // LA CITA APARTADA (D22-6): el único nodo que la reserva le agrega al
      // cierre bloqueado. Dice lo mismo que `confirmar` menos la firmeza, y
      // también el folio: una cita pendiente de confirmación se puede buscar
      // igual que una confirmada.
      ...(closesOnBooking
        ? [
            {
              id: AGENDA_PENDING_NODE,
              type: 'msg',
              position: { x: XA, y: y(rSalida) },
              config: { text: generatedAgendaConfirm('apartada', terminal, nameVar !== null) },
            } satisfies StarterNode,
          ]
        : []),
      // Sólo cuando el éxito TRASPASA: sin su arista de entrada este nodo es
      // huérfano y el validador rechaza el flujo entero (regla 3).
      ...(terminal === 'pasar'
        ? [
            {
              id: 'fin_traspaso',
              type: 'fin',
              position: { x: X, y: y(rFinal) },
              config: {
                kind: 'handoff',
                reason: 'lead_capturado',
                // `auto`, no un asesor con nombre: la cadena resuelve al responsable
                // de la tarjeta recién creada (peldaño 2), así que el traspaso sigue
                // siendo correcto cuando esa persona cambia.
                assign_to: { kind: 'auto' },
              },
            } satisfies StarterNode,
          ]
        : []),
      { id: 'fin_ok', type: 'fin', position: { x: XL, y: y(rSalida) }, config: { kind: 'ok' } },
      {
        id: 'avisar_asesor',
        type: 'msg',
        position: { x: XR, y: y(rSalida) },
        config: {
          text: 'Déjame pasarte con una persona del equipo para terminar de registrar tus datos.',
        },
      },
      {
        id: 'fin_handoff',
        type: 'fin',
        position: { x: XR, y: y(rFinal) },
        config: { kind: 'handoff', reason: 'No se pudo registrar el prospecto' },
      },
    ],
    edges,
  }
}
